import {
  mkdir,
  mkdtemp,
  open,
  readFile,
  readdir,
  rename,
  rm,
  stat,
  writeFile,
} from "node:fs/promises";
import { dirname, join } from "node:path";
import { tmpdir } from "node:os";

export async function ensureDirectory(path: string) {
  await mkdir(path, { recursive: true });
}

export async function readJsonFile<T>(path: string): Promise<T | undefined> {
  try {
    const content = await readFile(path, "utf8");
    return JSON.parse(content) as T;
  } catch (error) {
    if (isMissing(error)) {
      return undefined;
    }
    throw error;
  }
}

export async function writeJsonFileAtomic(path: string, value: unknown) {
  const directory = await mkdtemp(join(tmpdir(), "emergent-thinking-"));
  const tempPath = join(directory, "pending.json");
  await writeFile(tempPath, JSON.stringify(value, null, 2));
  await ensureDirectory(dirname(path));
  await rename(tempPath, path);
  await rm(directory, { force: true, recursive: true });
}

export async function writeTextFileAtomic(path: string, value: string) {
  const directory = await mkdtemp(join(tmpdir(), "emergent-thinking-"));
  const tempPath = join(directory, "pending.txt");
  await writeFile(tempPath, value);
  await ensureDirectory(dirname(path));
  await rename(tempPath, path);
  await rm(directory, { force: true, recursive: true });
}

export async function writeJsonFileExclusive(path: string, value: unknown) {
  await ensureDirectory(dirname(path));
  const handle = await open(path, "wx");
  try {
    await handle.writeFile(JSON.stringify(value, null, 2));
  } finally {
    await handle.close();
  }
}

export async function pathExists(path: string) {
  try {
    await stat(path);
    return true;
  } catch (error) {
    if (isMissing(error)) {
      return false;
    }
    throw error;
  }
}

export async function walkFiles(root: string, fileName: string): Promise<string[]> {
  const found: string[] = [];
  if (!(await pathExists(root))) {
    return found;
  }

  const queue = [root];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) {
      break;
    }

    const entries = await readdir(current, { withFileTypes: true });
    for (const entry of entries) {
      const next = join(current, entry.name);
      if (entry.isDirectory()) {
        queue.push(next);
        continue;
      }
      if (entry.isFile() && entry.name === fileName) {
        found.push(next);
      }
    }
  }

  return found.sort();
}

function isMissing(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "ENOENT"
  );
}
